import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PageHeader, Btn, Modal, Input } from "@/components/ui/kit";
import { toast } from "sonner";
import { Trash2, RotateCcw, Settings, Save, AlertTriangle } from "lucide-react";

const RESETS = [
  { key: "ventas", label: "Resetear Ventas", desc: "Elimina todo el historial de ventas" },
  { key: "pedidos", label: "Resetear Pedidos Online", desc: "Elimina todos los pedidos online" },
  { key: "inventario", label: "Resetear Inventario", desc: "Elimina todos los productos" },
  { key: "compras", label: "Resetear Compras", desc: "Elimina historial de compras" },
  { key: "gastos", label: "Resetear Gastos", desc: "Elimina todos los gastos" },
  { key: "transferencias", label: "Resetear Transferencias", desc: "Elimina historial de transferencias" },
  { key: "auditoria", label: "Limpiar Auditoría", desc: "Borra el registro de auditoría" },
  { key: "caja", label: "Resetear Caja", desc: "Elimina aperturas, retiros, ajustes y cierres de caja (Cierre Z e historial de arqueos)" },
  { key: "alertas", label: "Limpiar Alertas", desc: "Borra las alertas de diferencia de caja" },
  { key: "all", label: "Reseteo Total", desc: "Elimina ventas, pedidos, compras, gastos, transferencias, inventario, cajas y alertas", danger: true },
];

export default function Admin() {
  const [confirm, setConfirm] = useState(null);
  const [settings, setSettings] = useState({ ruc: "", bank_info: "", receipt_footer: "", store_hours: "", cash_diff_threshold: 0 });

  const [loaded, setLoaded] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => { setSettings(r.data); setLoaded(true); }).catch(() => {}); }, []);

  const doReset = async () => {
    try { await api.post("/admin/reset", { target: confirm.key }); toast.success("Reseteo completado: " + confirm.label); setConfirm(null); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveSettings = async () => {
    try { await api.put("/settings", settings); toast.success("Configuración guardada"); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Administración" subtitle="Configuración del sistema y herramientas de reseteo" />

      <div className="card-surface p-6 mb-6">
        <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Settings className="w-5 h-5 text-primary" /> Configuración general</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-sm font-medium mb-1 block">RUC / Timbrado</label><Input value={settings.ruc || ""} onChange={(e) => setSettings({ ...settings, ruc: e.target.value })} data-testid="set-ruc" /></div>
          <div><label className="text-sm font-medium mb-1 block">Horario de atención</label><Input value={settings.store_hours || ""} onChange={(e) => setSettings({ ...settings, store_hours: e.target.value })} data-testid="set-hours" placeholder="Lun-Sáb 7:00 a 20:00" /></div>
          <div className="md:col-span-2"><label className="text-sm font-medium mb-1 block">Datos para transferencias (banco, alias, cuenta)</label><Input value={settings.bank_info || ""} onChange={(e) => setSettings({ ...settings, bank_info: e.target.value })} data-testid="set-bank" placeholder="Banco Itaú · Cta 12345 · Alias nauta.cafe" /></div>
          <div><label className="text-sm font-medium mb-1 block">Tolerancia de diferencia en arqueo (Gs.)</label><Input type="number" min="0" disabled={!loaded} value={settings.cash_diff_threshold || ""} onChange={(e) => setSettings({ ...settings, cash_diff_threshold: Number(e.target.value) || 0 })} data-testid="set-cash-threshold" placeholder="Ej: 10000 (0 = sin alertas)" /><p className="text-[11px] text-muted-foreground mt-1">Si un cierre de caja supera este monto (faltante o sobrante), se alerta al administrador.</p></div>
          <div><label className="text-sm font-medium mb-1 block">Mensaje al pie del ticket</label><Input value={settings.receipt_footer || ""} onChange={(e) => setSettings({ ...settings, receipt_footer: e.target.value })} data-testid="set-footer" placeholder="¡Gracias por su compra!" /></div>
        </div>
        <Btn onClick={saveSettings} disabled={!loaded} data-testid="save-settings" className="mt-4"><Save className="w-4 h-4" /> Guardar configuración</Btn>
      </div>

      <div className="card-surface p-6 border-destructive/30">
        <h3 className="font-head font-bold mb-1 flex items-center gap-2 text-destructive"><AlertTriangle className="w-5 h-5" /> Zona de reseteo</h3>
        <p className="text-sm text-muted-foreground mb-4">Estas acciones son irreversibles. Úsalas con precaución.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {RESETS.map((r) => (
            <button key={r.key} onClick={() => setConfirm(r)} data-testid={`reset-${r.key}`}
              className={`text-left p-4 rounded-lg border transition-colors ${r.danger ? "border-destructive/40 hover:bg-destructive/10" : "border-border hover:bg-accent"}`}>
              <div className="flex items-center gap-2 font-semibold text-sm">{r.danger ? <Trash2 className="w-4 h-4 text-destructive" /> : <RotateCcw className="w-4 h-4 text-primary" />} {r.label}</div>
              <p className="text-xs text-muted-foreground mt-1">{r.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <Modal open={!!confirm} onClose={() => setConfirm(null)} title="Confirmar reseteo">
        {confirm && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-sm">
              <p className="font-semibold text-destructive flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {confirm.label}</p>
              <p className="text-muted-foreground mt-1">{confirm.desc}. Esta acción no se puede deshacer.</p>
            </div>
            <div className="flex gap-2">
              <Btn variant="outline" onClick={() => setConfirm(null)} className="flex-1">Cancelar</Btn>
              <Btn variant="danger" onClick={doReset} data-testid="confirm-reset" className="flex-1">Sí, resetear</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
